import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Plus, 
  Search, 
  ClipboardList,
  User,
  UserPlus,
  Calendar,
  FileText,
  Download,
  Eye,
  Edit,
  Trash2,
  Sparkles,
  AlertTriangle,
  Droplets,
  Palette,
  CheckCircle2,
  Clock,
  ChevronRight,
  Printer
} from "lucide-react";

// Opzioni predefinite
const TIPI_CAPELLO = ["Liscio", "Mosso", "Riccio", "Crespo"];
const SPESSORI = ["Fine", "Medio", "Grosso"];
const DENSITA = ["Scarsa", "Media", "Folta"];
const ELASTICITA = ["Scarsa", "Normale", "Buona"];
const POROSITA = ["Bassa", "Media", "Alta"];
const PERCENTUALI_BIANCHI = ["0%", "10-30%", "30-50%", "50-70%", "70-100%"];
const COLORI_NATURALI = ["Nero", "Castano Scuro", "Castano", "Castano Chiaro", "Biondo Scuro", "Biondo", "Biondo Chiaro", "Rosso"];
const STATI_CAPELLI = ["Normali", "Secchi", "Grassi", "Misti", "Danneggiati", "Trattati chimicamente"];
const PROBLEMI_CUOIO = ["Desquamazione", "Prurito", "Forfora", "Dermatite", "Sensibilità", "Eccesso sebo"];
const FREQUENZE_LAVAGGIO = ["Ogni giorno", "A giorni alterni", "2-3 volte/settimana", "1 volta/settimana"];

export default function ConsulenzeAvanzate() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consulenze, setConsulenze] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [operatori, setOperatori] = useState([]);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("tutti");
  
  // Dialog
  const [dialogConsulenza, setDialogConsulenza] = useState(false);
  const [dialogVisualizza, setDialogVisualizza] = useState(false);
  const [editingConsulenza, setEditingConsulenza] = useState(null);
  const [viewingConsulenza, setViewingConsulenza] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nome: "",
    tipo_cliente: "esistente",
    tipo_consulenza: "generale",
    // Anamnesi
    allergie: [],
    trattamenti_precedenti: "",
    problemi_cuoio_capelluto: [],
    frequenza_lavaggio: "",
    prodotti_usati_casa: "",
    aspettative: "",
    // Valutazione tecnica
    obiettivo: "",
    stato_capelli: "",
    tipo_capello: "",
    spessore_capello: "",
    densita: "",
    elasticita: "",
    porosita: "",
    percentuale_bianchi: "",
    colore_naturale: "",
    colore_attuale: "",
    // Problematiche e raccomandazioni
    problematiche: [],
    prodotti_consigliati: [],
    servizi_consigliati: [],
    note_tecniche: "",
    raccomandazioni: "",
    // Pianificazione
    prossimo_appuntamento: "",
    frequenza_consigliata: "",
    // Operatore
    operatore_id: "",
    operatore_nome: ""
  });
  
  const printRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [consulenzeRes, clientiRes, serviziRes, prodottiRes, operatoriRes] = await Promise.all([
        axios.get(`${API}/consulenze`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/servizi`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prodotti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/operatori`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConsulenze(consulenzeRes.data || []);
      setClienti(clientiRes.data || []);
      setServizi(serviziRes.data || []);
      setProdotti(prodottiRes.data || []);
      setOperatori(operatoriRes.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConsulenza = async () => {
    if (!formData.cliente_id) {
      toast.error("Seleziona un cliente");
      return;
    }

    try {
      if (editingConsulenza) {
        await axios.put(`${API}/consulenze/${editingConsulenza.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Consulenza aggiornata");
      } else {
        await axios.post(`${API}/consulenze`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Consulenza creata");
      }
      setDialogConsulenza(false);
      resetForm();
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteConsulenza = async (id) => {
    if (!confirm("Eliminare questa consulenza?")) return;
    try {
      await axios.delete(`${API}/consulenze/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Consulenza eliminata");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "", cliente_nome: "", tipo_cliente: "esistente", tipo_consulenza: "generale",
      allergie: [], trattamenti_precedenti: "", problemi_cuoio_capelluto: [], frequenza_lavaggio: "",
      prodotti_usati_casa: "", aspettative: "", obiettivo: "", stato_capelli: "", tipo_capello: "",
      spessore_capello: "", densita: "", elasticita: "", porosita: "", percentuale_bianchi: "",
      colore_naturale: "", colore_attuale: "", problematiche: [], prodotti_consigliati: [],
      servizi_consigliati: [], note_tecniche: "", raccomandazioni: "", prossimo_appuntamento: "",
      frequenza_consigliata: "", operatore_id: "", operatore_nome: ""
    });
    setEditingConsulenza(null);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('consulenza-print');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Impossibile aprire finestra di stampa. Controlla i popup blocker.");
        return;
      }
      
      // Costruzione sicura del documento di stampa usando DOM API
      const doc = printWindow.document;
      
      // Crea gli elementi in modo sicuro (no XSS)
      const doctype = document.implementation.createDocumentType('html', '', '');
      const html = doc.createElement('html');
      const head = doc.createElement('head');
      const title = doc.createElement('title');
      const style = doc.createElement('style');
      const body = doc.createElement('body');
      const printContainer = doc.createElement('div');
      const footer = doc.createElement('div');
      
      // Sanitizza il nome cliente per il titolo
      const clienteName = (viewingConsulenza?.cliente_nome || '').replace(/[<>&"']/g, '');
      title.textContent = `Scheda Consulenza - ${clienteName}`;
      
      style.textContent = `
        body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
        h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
        h2 { color: #374151; margin-top: 20px; font-size: 16px; background: #f3f4f6; padding: 8px; }
        .section { margin-bottom: 15px; }
        .row { display: flex; margin-bottom: 8px; }
        .label { font-weight: bold; width: 180px; color: #6b7280; }
        .value { flex: 1; }
        .badge { display: inline-block; background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; margin-right: 5px; font-size: 12px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      `;
      
      footer.className = 'footer';
      footer.textContent = `I CIRILLO PARRUCCHIERI - Scheda Consulenza generata il ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: it })}`;
      
      // Clona il contenuto invece di usare innerHTML direttamente
      const clonedContent = printContent.cloneNode(true);
      printContainer.appendChild(clonedContent);
      
      // Assembla il documento
      head.appendChild(title);
      head.appendChild(style);
      body.appendChild(printContainer);
      body.appendChild(footer);
      html.appendChild(head);
      html.appendChild(body);
      
      // Sostituisci il documento
      doc.replaceChild(html, doc.documentElement);
      
      // Stampa dopo che il contenuto è stato caricato
      printWindow.onload = () => printWindow.print();
    }
  };

  const consulenzeFiltrate = consulenze.filter(c => {
    if (searchTerm && !c.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filtroTipo !== "tutti" && c.tipo_cliente !== filtroTipo) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consulenze-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-purple-600" />
            Schede Consulenza
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestisci le consulenze per clienti nuovi ed esistenti
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogConsulenza(true);
          }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova Consulenza
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Totale Consulenze</p>
                <p className="text-2xl font-bold text-purple-800">{consulenze.length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Nuovi Clienti</p>
                <p className="text-2xl font-bold text-green-800">
                  {consulenze.filter(c => c.tipo_cliente === 'nuovo').length}
                </p>
              </div>
              <UserPlus className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Clienti Esistenti</p>
                <p className="text-2xl font-bold text-blue-800">
                  {consulenze.filter(c => c.tipo_cliente === 'esistente').length}
                </p>
              </div>
              <User className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Questo Mese</p>
                <p className="text-2xl font-bold text-amber-800">
                  {consulenze.filter(c => {
                    const data = new Date(c.created_at);
                    const now = new Date();
                    return data.getMonth() === now.getMonth() && data.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri e Lista */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg">Lista Consulenze</CardTitle>
            <div className="flex gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i tipi</SelectItem>
                  <SelectItem value="nuovo">Nuovi Clienti</SelectItem>
                  <SelectItem value="esistente">Esistenti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {consulenzeFiltrate.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nessuna consulenza trovata</p>
              <p className="text-sm mt-1">Crea una nuova consulenza per iniziare</p>
            </div>
          ) : (
            <div className="space-y-3">
              {consulenzeFiltrate.map(consulenza => (
                <div 
                  key={consulenza.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => {
                    setViewingConsulenza(consulenza);
                    setDialogVisualizza(true);
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      consulenza.tipo_cliente === 'nuovo' 
                        ? 'bg-green-100' 
                        : 'bg-blue-100'
                    }`}>
                      {consulenza.tipo_cliente === 'nuovo' 
                        ? <UserPlus className="w-6 h-6 text-green-600" />
                        : <User className="w-6 h-6 text-blue-600" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{consulenza.cliente_nome}</span>
                        <Badge variant="outline" className={
                          consulenza.tipo_cliente === 'nuovo'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }>
                          {consulenza.tipo_cliente === 'nuovo' ? 'Nuovo' : 'Esistente'}
                        </Badge>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {consulenza.tipo_consulenza}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {consulenza.created_at ? format(new Date(consulenza.created_at), 'dd/MM/yyyy', { locale: it }) : '-'}
                        </span>
                        {consulenza.operatore_nome && (
                          <span>Operatore: {consulenza.operatore_nome}</span>
                        )}
                        {consulenza.stato_capelli && (
                          <span className="flex items-center gap-1">
                            <Droplets className="w-3 h-3" />
                            {consulenza.stato_capelli}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingConsulenza(consulenza);
                        setFormData(consulenza);
                        setDialogConsulenza(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConsulenza(consulenza.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuova/Modifica Consulenza */}
      <Dialog open={dialogConsulenza} onOpenChange={setDialogConsulenza}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              {editingConsulenza ? 'Modifica Consulenza' : 'Nuova Consulenza'}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[70vh] pr-4">
            <div className="space-y-6">
              {/* Tipo Cliente e Selezione */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                <div>
                  <Label>Tipo Cliente *</Label>
                  <Select
                    value={formData.tipo_cliente}
                    onValueChange={(v) => setFormData({...formData, tipo_cliente: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nuovo">
                        <span className="flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-green-600" />
                          Nuovo Cliente
                        </span>
                      </SelectItem>
                      <SelectItem value="esistente">
                        <span className="flex items-center gap-2">
                          <User className="w-4 h-4 text-blue-600" />
                          Cliente Esistente
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cliente *</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(v) => {
                      const c = clienti.find(c => c.id === v);
                      setFormData({
                        ...formData, 
                        cliente_id: v, 
                        cliente_nome: c ? `${c.nome} ${c.cognome}` : ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clienti.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nome} {c.cognome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo Consulenza</Label>
                  <Select
                    value={formData.tipo_consulenza}
                    onValueChange={(v) => setFormData({...formData, tipo_consulenza: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="generale">Generale</SelectItem>
                      <SelectItem value="colore">Colore</SelectItem>
                      <SelectItem value="taglio">Taglio</SelectItem>
                      <SelectItem value="trattamento">Trattamento</SelectItem>
                      <SelectItem value="prodotti">Prodotti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Operatore</Label>
                  <Select
                    value={formData.operatore_id}
                    onValueChange={(v) => {
                      const op = operatori.find(o => o.id === v);
                      setFormData({...formData, operatore_id: v, operatore_nome: op?.nome || ''});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona operatore" />
                    </SelectTrigger>
                    <SelectContent>
                      {operatori.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* ANAMNESI - Solo per nuovi clienti o espandibile */}
              {formData.tipo_cliente === 'nuovo' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Anamnesi (Nuovo Cliente)
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Allergie Note</Label>
                      <Input
                        placeholder="Es: nichel, ammoniaca..."
                        value={formData.allergie?.join(', ') || ''}
                        onChange={(e) => setFormData({
                          ...formData, 
                          allergie: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                      />
                    </div>
                    <div>
                      <Label>Frequenza Lavaggio</Label>
                      <Select
                        value={formData.frequenza_lavaggio}
                        onValueChange={(v) => setFormData({...formData, frequenza_lavaggio: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENZE_LAVAGGIO.map(f => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Trattamenti Precedenti (fatti altrove)</Label>
                      <Textarea
                        value={formData.trattamenti_precedenti}
                        onChange={(e) => setFormData({...formData, trattamenti_precedenti: e.target.value})}
                        placeholder="Descrivi eventuali trattamenti fatti in altri saloni..."
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Problemi Cuoio Capelluto</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {PROBLEMI_CUOIO.map(p => (
                          <label key={p} className="flex items-center gap-1.5 text-sm">
                            <Checkbox
                              checked={formData.problemi_cuoio_capelluto?.includes(p)}
                              onCheckedChange={(checked) => {
                                const current = formData.problemi_cuoio_capelluto || [];
                                setFormData({
                                  ...formData,
                                  problemi_cuoio_capelluto: checked 
                                    ? [...current, p]
                                    : current.filter(x => x !== p)
                                });
                              }}
                            />
                            {p}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Prodotti Usati a Casa</Label>
                      <Input
                        value={formData.prodotti_usati_casa}
                        onChange={(e) => setFormData({...formData, prodotti_usati_casa: e.target.value})}
                        placeholder="Marche/prodotti utilizzati"
                      />
                    </div>
                    <div>
                      <Label>Aspettative del Cliente</Label>
                      <Input
                        value={formData.aspettative}
                        onChange={(e) => setFormData({...formData, aspettative: e.target.value})}
                        placeholder="Cosa si aspetta dal salone"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* VALUTAZIONE TECNICA */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Valutazione Tecnica
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Tipo Capello</Label>
                    <Select value={formData.tipo_capello} onValueChange={(v) => setFormData({...formData, tipo_capello: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {TIPI_CAPELLO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Stato Capelli</Label>
                    <Select value={formData.stato_capelli} onValueChange={(v) => setFormData({...formData, stato_capelli: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {STATI_CAPELLI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Spessore</Label>
                    <Select value={formData.spessore_capello} onValueChange={(v) => setFormData({...formData, spessore_capello: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {SPESSORI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Densità</Label>
                    <Select value={formData.densita} onValueChange={(v) => setFormData({...formData, densita: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {DENSITA.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Elasticità</Label>
                    <Select value={formData.elasticita} onValueChange={(v) => setFormData({...formData, elasticita: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {ELASTICITA.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Porosità</Label>
                    <Select value={formData.porosita} onValueChange={(v) => setFormData({...formData, porosita: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {POROSITA.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>% Capelli Bianchi</Label>
                    <Select value={formData.percentuale_bianchi} onValueChange={(v) => setFormData({...formData, percentuale_bianchi: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {PERCENTUALI_BIANCHI.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Colore Naturale</Label>
                    <Select value={formData.colore_naturale} onValueChange={(v) => setFormData({...formData, colore_naturale: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {COLORI_NATURALI.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Colore Attuale</Label>
                    <Input
                      value={formData.colore_attuale}
                      onChange={(e) => setFormData({...formData, colore_attuale: e.target.value})}
                      placeholder="Se diverso da naturale"
                    />
                  </div>
                </div>
              </div>

              {/* OBIETTIVI E RACCOMANDAZIONI */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Obiettivi e Raccomandazioni
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Obiettivo del Cliente</Label>
                    <Textarea
                      value={formData.obiettivo}
                      onChange={(e) => setFormData({...formData, obiettivo: e.target.value})}
                      placeholder="Cosa desidera ottenere il cliente..."
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Note Tecniche</Label>
                    <Textarea
                      value={formData.note_tecniche}
                      onChange={(e) => setFormData({...formData, note_tecniche: e.target.value})}
                      placeholder="Osservazioni tecniche del professionista..."
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Raccomandazioni</Label>
                    <Textarea
                      value={formData.raccomandazioni}
                      onChange={(e) => setFormData({...formData, raccomandazioni: e.target.value})}
                      placeholder="Consigli per il cliente..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Prossimo Appuntamento Consigliato</Label>
                    <Input
                      type="date"
                      value={formData.prossimo_appuntamento}
                      onChange={(e) => setFormData({...formData, prossimo_appuntamento: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Frequenza Consigliata</Label>
                    <Input
                      value={formData.frequenza_consigliata}
                      onChange={(e) => setFormData({...formData, frequenza_consigliata: e.target.value})}
                      placeholder="Es: ogni 4-6 settimane"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogConsulenza(false)}>Annulla</Button>
            <Button onClick={handleSaveConsulenza} className="bg-purple-600 hover:bg-purple-700">
              {editingConsulenza ? 'Aggiorna' : 'Salva'} Consulenza
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizza Consulenza */}
      <Dialog open={dialogVisualizza} onOpenChange={setDialogVisualizza}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-purple-600" />
                Scheda Consulenza
              </DialogTitle>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Stampa PDF
              </Button>
            </div>
          </DialogHeader>
          
          {viewingConsulenza && (
            <ScrollArea className="h-[70vh]">
              <div id="consulenza-print" className="space-y-6 pr-4">
                {/* Header */}
                <div className="text-center pb-4 border-b">
                  <h1 className="text-xl font-bold text-purple-800">SCHEDA CONSULENZA</h1>
                  <p className="text-slate-500">I CIRILLO PARRUCCHIERI</p>
                </div>
                
                {/* Info Cliente */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h2 className="font-semibold text-slate-700 mb-3">Dati Cliente</h2>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Nome:</span> <strong>{viewingConsulenza.cliente_nome}</strong></div>
                    <div><span className="text-slate-500">Tipo:</span> <Badge className={viewingConsulenza.tipo_cliente === 'nuovo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>{viewingConsulenza.tipo_cliente === 'nuovo' ? 'Nuovo Cliente' : 'Cliente Esistente'}</Badge></div>
                    <div><span className="text-slate-500">Data:</span> {viewingConsulenza.created_at ? format(new Date(viewingConsulenza.created_at), 'dd/MM/yyyy', { locale: it }) : '-'}</div>
                    <div><span className="text-slate-500">Operatore:</span> {viewingConsulenza.operatore_nome || '-'}</div>
                  </div>
                </div>

                {/* Anamnesi (se nuovo cliente) */}
                {viewingConsulenza.tipo_cliente === 'nuovo' && (
                  <div>
                    <h2 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Anamnesi
                    </h2>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {viewingConsulenza.allergie?.length > 0 && (
                        <div><span className="text-slate-500">Allergie:</span> {viewingConsulenza.allergie.join(', ')}</div>
                      )}
                      {viewingConsulenza.frequenza_lavaggio && (
                        <div><span className="text-slate-500">Frequenza lavaggio:</span> {viewingConsulenza.frequenza_lavaggio}</div>
                      )}
                      {viewingConsulenza.trattamenti_precedenti && (
                        <div className="col-span-2"><span className="text-slate-500">Trattamenti precedenti:</span> {viewingConsulenza.trattamenti_precedenti}</div>
                      )}
                      {viewingConsulenza.problemi_cuoio_capelluto?.length > 0 && (
                        <div className="col-span-2"><span className="text-slate-500">Problemi cuoio capelluto:</span> {viewingConsulenza.problemi_cuoio_capelluto.join(', ')}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Valutazione Tecnica */}
                <div>
                  <h2 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Valutazione Tecnica
                  </h2>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {viewingConsulenza.tipo_capello && <div><span className="text-slate-500">Tipo:</span> {viewingConsulenza.tipo_capello}</div>}
                    {viewingConsulenza.stato_capelli && <div><span className="text-slate-500">Stato:</span> {viewingConsulenza.stato_capelli}</div>}
                    {viewingConsulenza.spessore_capello && <div><span className="text-slate-500">Spessore:</span> {viewingConsulenza.spessore_capello}</div>}
                    {viewingConsulenza.densita && <div><span className="text-slate-500">Densità:</span> {viewingConsulenza.densita}</div>}
                    {viewingConsulenza.elasticita && <div><span className="text-slate-500">Elasticità:</span> {viewingConsulenza.elasticita}</div>}
                    {viewingConsulenza.porosita && <div><span className="text-slate-500">Porosità:</span> {viewingConsulenza.porosita}</div>}
                    {viewingConsulenza.percentuale_bianchi && <div><span className="text-slate-500">% Bianchi:</span> {viewingConsulenza.percentuale_bianchi}</div>}
                    {viewingConsulenza.colore_naturale && <div><span className="text-slate-500">Colore naturale:</span> {viewingConsulenza.colore_naturale}</div>}
                    {viewingConsulenza.colore_attuale && <div><span className="text-slate-500">Colore attuale:</span> {viewingConsulenza.colore_attuale}</div>}
                  </div>
                </div>

                {/* Obiettivi e Raccomandazioni */}
                <div>
                  <h2 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Obiettivi e Raccomandazioni
                  </h2>
                  <div className="space-y-3 text-sm">
                    {viewingConsulenza.obiettivo && (
                      <div><span className="text-slate-500 block">Obiettivo:</span> <p className="mt-1">{viewingConsulenza.obiettivo}</p></div>
                    )}
                    {viewingConsulenza.note_tecniche && (
                      <div><span className="text-slate-500 block">Note tecniche:</span> <p className="mt-1">{viewingConsulenza.note_tecniche}</p></div>
                    )}
                    {viewingConsulenza.raccomandazioni && (
                      <div><span className="text-slate-500 block">Raccomandazioni:</span> <p className="mt-1">{viewingConsulenza.raccomandazioni}</p></div>
                    )}
                    {viewingConsulenza.frequenza_consigliata && (
                      <div><span className="text-slate-500">Frequenza consigliata:</span> {viewingConsulenza.frequenza_consigliata}</div>
                    )}
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
